const HERO_IMAGE =
  'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?auto=format&fit=crop&w=1600&q=80';

const RELATED_GUIDES = [
  {
    slug: '7v7-defender-positioning-pressing-guide',
    title: '7v7 Defenders: Positioning & Pressing When the Other Team Attacks',
    blurb: 'compact shape, press triggers, and animated diagrams.',
  },
  {
    slug: 'soccer-goalkeeper-drills-free-youtube-videos',
    title: 'Soccer Goalkeeper Drills + Free YouTube Training Videos',
    blurb: 'handling, diving, and distribution.',
  },
  {
    slug: 'soccer-forward-striker-drills-free-youtube-videos',
    title: 'Soccer Forward & Striker Drills + Free YouTube Videos',
    blurb: 'finishing, movement, and 1v1 attacking.',
  },
];

/** Featured guide: defender drills with free YouTube training videos */
export const DEFENDER_DRILLS_POST = {
  id: 'featured-defender-drills-youtube',
  slug: 'soccer-defender-drills-free-youtube-videos',
  title: 'Soccer Defender Drills + Free YouTube Training Videos',
  excerpt:
    '1v1 defending, jockeying, tackling, positioning, aerial duels, and recovery runs—drills for center backs and fullbacks with links to free YouTube sessions.',
  publishedAt: '2026-07-22T00:35:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Defender Training Desk',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'More position training guides',
  relatedPosts: RELATED_GUIDES,
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '7 areas', label: '1v1 · Tackling · Positioning · More' },
        { value: '18+', label: 'Drill ideas' },
        { value: '10', label: 'Free YouTube sessions' },
        { value: 'All levels', label: 'Rec to competitive' },
      ],
    },
    {
      type: 'panel',
      title: 'How to use this guide',
      paragraphs: [
        'Written for center backs, fullbacks, and defensive mids who want structured reps without a full coaching staff. Each section pairs field drills with free YouTube videos from channels like Unisport, Progressive Soccer, and Train Effective.',
        'Defend with patience first—delay the attacker, stay goal-side, and tackle only when you are sure to win the ball.',
      ],
      chips: ['Free on YouTube', 'Solo or partner', 'Rec & club', 'Ohio teams'],
    },
    {
      type: 'panel',
      title: '1 — Stance, footwork & jockeying',
      list: [
        'Side-on stance: knees bent, on balls of feet, show attacker to one side.',
        'Mirror shuffle: attacker moves laterally 10 yds; defender mirrors without crossing feet.',
        'Close-down then retreat: sprint 3 steps out, chop back into jockey—repeat 8 times.',
        'Channel drill: two cones 2 yds apart; force attacker through narrow side.',
        'Body shape check: can you see ball and attacker in one glance? If not, adjust angle.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — defending fundamentals',
      videos: [
        {
          title: 'Dominate Defense: Instant Tips for Better Soccer Defending',
          channel: 'Progressive Soccer',
          description: 'Core body shape, pressure, and patience when closing down attackers.',
          url: 'https://www.youtube.com/watch?v=hFSmx8BE-uk',
        },
        {
          title: 'How to STOP fast skillful attackers',
          channel: 'Unisport',
          description: 'Angle your body and delay speed dribblers without diving in.',
          url: 'https://www.youtube.com/watch?v=K17wDaDcuyY',
        },
        {
          title: 'Best Soccer Defensive Drills!',
          channel: 'ABF Productions',
          description: 'Quick drill ideas you can set up with cones and one partner.',
          url: 'https://www.youtube.com/watch?v=QWX-K0t1FJc',
        },
      ],
    },
    {
      type: 'panel',
      title: '2 — 1v1 defending',
      list: [
        'Gate 1v1: attacker starts between two cones 25 yds from mini goal; defender delays to win ball or force wide shot.',
        'Recovery run: defender starts 5 yds behind; attacker released on through ball—recover goal-side.',
        'Show inside / show outside: coach calls which foot to force; defender adjusts hip angle.',
        'Block tackle window: only tackle after attacker takes heavy touch or looks down.',
        'Transition: after win, play to target cone in 3 seconds—train defending then passing.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — 1v1 defending',
      videos: [
        {
          title: 'MASTER 1v1 DEFENDING with these tips!',
          channel: 'Train Effective',
          description: 'Step-by-step 1v1 approach used by pro coaches.',
          url: 'https://www.youtube.com/watch?v=CFc0V47Nzng',
        },
        {
          title: 'How To Defend 1v1s - 3 Tips From Rio Ferdinand',
          channel: 'Train Effective',
          description: 'Legendary center back breaks down timing and distance.',
          url: 'https://www.youtube.com/watch?v=vPg5huwdbp4',
        },
        {
          title: 'Master 1v1 Defending Like a Pro!',
          channel: 'PFP PLAYER',
          description: 'High-energy 1v1 session with clear coaching points.',
          url: 'https://www.youtube.com/watch?v=CEskL_LgBFM',
        },
      ],
    },
    {
      type: 'panel',
      title: '3 — Tackling & ball winning',
      list: [
        'Block tackle: plant foot, slide through ball center, lock ankle—practice on soft grass first.',
        'Poke tackle: jab ball with toes when it is exposed; stay on feet.',
        'Shoulder-to-shoulder: win aerial duels legally with arm distance and core strength.',
        '2v2 rondo defend: two defenders protect square; win ball and play out.',
        'Clean vs. foul: if you miss ball and hit player, it is a foul—rep slow then add speed.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — tackling technique',
      videos: [
        {
          title: 'Unlock Your Defensive Skills: Essential Drills for Defenders!',
          channel: 'Soccer Training Tips (Train Effective)',
          description: 'Full defender drill session covering tackles and shape.',
          url: 'https://www.youtube.com/watch?v=0pC2xwU72gw',
        },
      ],
    },
    {
      type: 'panel',
      title: '4 — Positioning & line discipline',
      list: [
        'Back-four line: four defenders hold a rope (imaginary)—step up and drop together on coach call.',
        'Offside trap timing: hold line, sprint up together when ball is played—only if your league uses offside.',
        'Cover and balance: nearest presses, second covers central space, third holds depth.',
        'Switch play reaction: when ball travels wide, shift as a unit; far-side defender tucks in.',
        'Scanning: before receiving back pass, open body to see striker and midfield.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — team defending shape',
      videos: [
        {
          title: 'Soccer Offside Trap: Defend Like a Pro!',
          channel: 'Progressive Soccer',
          description: 'Line discipline and coordinated step-up for a high defensive line.',
          url: 'https://www.youtube.com/watch?v=SlLAQ63KoBs',
        },
        {
          title: '7v7 Defending',
          channel: 'Soccer Made Easy',
          description: 'Compact shape and pressing triggers in small-sided games.',
          url: 'https://www.youtube.com/watch?v=LzCZ858V5AI',
        },
      ],
    },
    {
      type: 'panel',
      title: '5 — Aerial duels & clearances',
      list: [
        'Attack the ball: jump early, lead with forearm for space (legal distance), head through ball.',
        'Defensive header: head up and away; aim wide of goal on corners.',
        'Wall positioning: near post, far post, and edge assignments—call every ball.',
        'Drop ball under pressure: head sideways to fullback or long to wing.',
        'Partner toss: server throws; defender times jump and clears—8 reps each side.',
      ],
    },
    {
      type: 'panel',
      title: '6 — Speed & recovery runs',
      list: [
        'Sprint-backpedal-sprint: 10 yds each; stay low in transitions.',
        'Recovery to goal: start at halfway facing wrong way; turn and sprint to 6-yard line on whistle.',
        'Slide then recover: controlled slide, pop up, sprint to cone—build match fitness.',
        'Shadow striker: live with striker for 5 minutes—never let them get goal-side.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — speed for defenders',
      videos: [
        {
          title: 'Speed Session For Defenders',
          channel: 'Cooket5training',
          description: 'Acceleration and agility patterns built for fullbacks and center backs.',
          url: 'https://www.youtube.com/watch?v=Qo_kHA8xvbo',
        },
      ],
    },
    {
      type: 'panel',
      title: '7 — Sample 60-minute defender session',
      orderedList: [
        '10 min — footwork mirror and jockeying (section 1).',
        '15 min — 1v1 gates to mini goals (section 2).',
        '10 min — aerial tosses and clearing headers (section 5).',
        '15 min — 7v7 or 4v4 with focus on line movement (section 4).',
        '10 min — recovery sprints and cool-down stretch.',
      ],
      callout:
        'Pair with our Ohio open fields guide to find a park for evening defensive sessions.',
    },
  ],
  referenceLinks: [
    {
      label: 'Unisport — YouTube channel',
      url: 'https://www.youtube.com/@Unisport',
    },
    {
      label: 'Progressive Soccer — YouTube channel',
      url: 'https://www.youtube.com/@ProgressiveSoccer',
    },
    {
      label: 'Train Effective — YouTube channel',
      url: 'https://www.youtube.com/@TrainEffective',
    },
    {
      label: 'Open soccer fields near Powell, Dublin, Lewis Center & Delaware',
      url: 'https://dailysportslife.com/blog/open-soccer-fields-powell-dublin-lewis-center-delaware-ohio',
    },
  ],
};
